import React from "react";
import {
  CollectFieldsResponse,
  FieldDescription,
  VerificationStep,
  PersonalInfoViewModel,
  CollectFieldsAction,
} from "../types/types";
import { getSafe } from "../utils/getSafe";
import { logger } from "../utils/logger/logger";
import { getFieldsToCollect } from "./VerificationApiUtils";
import { useAppDispatch } from "../state/contextHooks";

export const useCollectFieldsEndpoint = <VM extends PersonalInfoViewModel = PersonalInfoViewModel>({
  verificationId,
  currentStep,
  viewModel,
  defaultRequiredFields = [],
}: {
  verificationId: string;
  currentStep: VerificationStep;
  viewModel: VM;
  defaultRequiredFields?: FieldDescription[];
}) => {
  const dispatch = useAppDispatch();
  const status = "status" in viewModel ? viewModel.status : undefined;

  const [requiredFields, setRequiredFields] =
    React.useState<FieldDescription[]>(defaultRequiredFields);
  const [optionalFields, setOptionalFields] = React.useState<FieldDescription[]>([]);

  const isFieldRequired = (key: keyof VM) => requiredFields.some((field) => field.key === key);
  const isFieldOptional = (key: keyof VM) => optionalFields.some((field) => field.key === key);

  React.useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        const collectFieldsResponse: CollectFieldsResponse = await getFieldsToCollect(
          verificationId,
          currentStep,
          viewModel,
        );
        const newRequiredFields = getSafe(() => collectFieldsResponse.fieldsToCollect.required, []);
        const newOptionalFields = getSafe(() => collectFieldsResponse.fieldsToCollect.optional, []);

        if (!abortController.signal.aborted) {
          setRequiredFields(newRequiredFields);
          setOptionalFields(newOptionalFields);

          // Dispatch the field requirements to the reducer
          const collectFieldsAction: CollectFieldsAction = {
            type: "COLLECT_FIELDS",
            requiredFields: newRequiredFields,
            optionalFields: newOptionalFields,
          };
          dispatch(collectFieldsAction);
        }
      } catch (error) {
        logger.error(`Failed to determine fields to collect: ${error}`, currentStep);
      }
    })();

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [viewModel.countryChoice?.value, status]);

  return { isFieldRequired, isFieldOptional };
};
